import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  Plus, Trash2, RotateCw, Music2, UtensilsCrossed, Wine, Gift, Sparkles, DoorOpen, Cake,
  Copy, Pencil, MoreVertical, MapPin, Users, ArmchairIcon, PenLine, Eraser, Shapes, Ruler,
} from "lucide-react";
import { toast } from "sonner";

const SHAPES = [
  { value: "round", label: "Round", defaultW: 120, defaultH: 120 },
  { value: "square", label: "Square", defaultW: 110, defaultH: 110 },
  { value: "rectangle", label: "Rectangle", defaultW: 160, defaultH: 90 },
  { value: "oval", label: "Oval", defaultW: 170, defaultH: 100 },
  { value: "long", label: "Long / Head table", defaultW: 220, defaultH: 60 },
];

const DECOR_TYPES = [
  { type: "dance-floor", label: "Dance Floor", w: 200, h: 200, icon: Sparkles, color: "#0A0A0A" },
  { type: "dj-booth", label: "DJ Booth", w: 120, h: 60, icon: Music2, color: "#0A0A0A" },
  { type: "stage", label: "Stage", w: 200, h: 80, icon: Sparkles, color: "#0A0A0A" },
  { type: "bar", label: "Bar", w: 180, h: 60, icon: Wine, color: "#B45309" },
  { type: "buffet", label: "Buffet", w: 200, h: 60, icon: UtensilsCrossed, color: "#B45309" },
  { type: "cake-table", label: "Cake Table", w: 100, h: 60, icon: Cake, color: "#DC2626" },
  { type: "gift-table", label: "Gift Table", w: 120, h: 50, icon: Gift, color: "#71717A" },
  { type: "entrance", label: "Entrance", w: 100, h: 30, icon: DoorOpen, color: "#71717A" },
];

const SEATING_MODES = [
  { value: "assigned", label: "Assigned seating", helper: "Guests get placed at specific tables (classic reception setup)." },
  { value: "open", label: "Open seating", helper: "Tables exist but nobody is assigned — sit-where-you-like." },
  { value: "standing", label: "Standing / cocktail", helper: "No fixed tables. Just track headcount and capacity." },
  { value: "rows", label: "Rows / ceremony", helper: "Rows of chairs — great for ceremony aisles or panels." },
];

function modeLabel(v) {
  return (SEATING_MODES.find(m => m.value === v) || SEATING_MODES[0]).label;
}

export default function SeatingTab({ eventId, ev, canEdit }) {
  const [spaces, setSpaces] = useState([]);
  const [activeSpaceId, setActiveSpaceId] = useState(null);
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [tableDialog, setTableDialog] = useState(false);
  const [form, setForm] = useState({ name: "", shape: "round", capacity: 8 });
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const canvasRef = useRef(null);
  const dragging = useRef(null);

  // Space create / rename dialogs
  const [newSpaceOpen, setNewSpaceOpen] = useState(false);
  const [newSpaceForm, setNewSpaceForm] = useState({ name: "", seating_mode: "assigned", width: 900, height: 600 });
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [dupOpen, setDupOpen] = useState(false);
  const [dupName, setDupName] = useState("");
  const [dupCopyTables, setDupCopyTables] = useState(true);

  // Freehand room-shape drawing
  const [drawMode, setDrawMode] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState(null); // null when idle, array during a stroke
  const strokeRef = useRef(null);

  // Snap-to-orthogonal toggle — persisted per-browser
  const SNAP_KEY = "eventlab.room_draw_snap";
  const [snap45, setSnap45] = useState(() => {
    try { return localStorage.getItem(SNAP_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(SNAP_KEY, snap45 ? "1" : "0"); } catch (e) { /* quota / privacy-mode */ console.debug("localStorage set failed", e); }
  }, [snap45]);

  // Custom decor element dialog
  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ label: "", shape: "rect", w: 120, h: 80, color: "#0A0A0A" });

  const activeSpace = spaces.find(s => s.id === activeSpaceId) || null;
  const mode = activeSpace?.seating_mode || "assigned";

  // ---- Load ----
  const loadSpaces = useCallback(async (preferredId) => {
    try {
      const { data } = await api.get(`/events/${eventId}/spaces`);
      setSpaces(data);
      if (data.length > 0) {
        const keep = preferredId && data.some(s => s.id === preferredId) ? preferredId : data[0].id;
        setActiveSpaceId(keep);
      }
    } catch (e) {
      console.error("load spaces failed:", e);
      toast.error("Could not load floor plans");
    }
  }, [eventId]);

  const loadGuests = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/guests`);
      setGuests(data);
    } catch (e) { console.error("load guests failed:", e); }
  }, [eventId]);

  const loadTables = useCallback(async (spaceId) => {
    if (!spaceId) { setTables([]); return; }
    try {
      const { data } = await api.get(`/events/${eventId}/tables?space_id=${spaceId}`);
      setTables(data);
      setSelectedTable(null);
      setSelectedElement(null);
    } catch (e) { console.error("load tables failed:", e); }
  }, [eventId]);

  useEffect(() => { loadSpaces(); loadGuests(); }, [loadSpaces, loadGuests]);
  useEffect(() => { if (activeSpaceId) loadTables(activeSpaceId); }, [activeSpaceId, loadTables]);

  // ---- Space CRUD ----
  const patchSpace = useCallback(async (patch) => {
    if (!activeSpaceId) return;
    // Optimistic update
    setSpaces(prev => prev.map(s => s.id === activeSpaceId ? { ...s, ...patch } : s));
    try {
      await api.patch(`/events/${eventId}/spaces/${activeSpaceId}`, patch);
    } catch (e) {
      console.error("patch space failed:", e);
      toast.error("Could not save floor plan");
    }
  }, [eventId, activeSpaceId]);

  const createSpace = async () => {
    const name = (newSpaceForm.name || "").trim();
    if (!name) { toast.error("Give the space a name"); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/spaces`, {
        name,
        seating_mode: newSpaceForm.seating_mode,
        width: Math.max(300, Math.min(2000, Number(newSpaceForm.width) || 900)),
        height: Math.max(300, Math.min(2000, Number(newSpaceForm.height) || 600)),
      });
      setSpaces(prev => [...prev, data]);
      setActiveSpaceId(data.id);
      setNewSpaceOpen(false);
      setNewSpaceForm({ name: "", seating_mode: "assigned", width: 900, height: 600 });
      toast.success(`Added "${data.name}"`);
    } catch (e) {
      console.error("create space failed:", e);
      toast.error("Could not add space");
    }
  };

  const renameSpace = async () => {
    const name = (renameValue || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    await patchSpace({ name });
    setRenameOpen(false);
    toast.success("Renamed");
  };

  const duplicateSpace = async () => {
    if (!activeSpace) return;
    try {
      const { data } = await api.post(`/events/${eventId}/spaces/${activeSpace.id}/duplicate`, {
        name: (dupName || "").trim() || `${activeSpace.name} v2`,
        copy_tables: dupCopyTables,
      });
      setSpaces(prev => [...prev, data.space]);
      setActiveSpaceId(data.space.id);
      setDupOpen(false);
      setDupName("");
      toast.success(`Duplicated → ${data.space.name}` + (data.tables_copied ? ` · ${data.tables_copied} tables` : ""));
    } catch (e) {
      console.error("duplicate failed:", e);
      toast.error("Could not duplicate");
    }
  };

  const deleteSpace = async () => {
    if (!activeSpace) return;
    if (spaces.length <= 1) { toast.error("Add another space first"); return; }
    if (!confirm(`Delete "${activeSpace.name}" and its tables? Guests seated there will be unassigned.`)) return;
    try {
      await api.delete(`/events/${eventId}/spaces/${activeSpace.id}`);
      const remaining = spaces.filter(s => s.id !== activeSpace.id);
      setSpaces(remaining);
      setActiveSpaceId(remaining[0]?.id || null);
      toast.success("Space deleted");
      loadGuests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const changeMode = async (nextMode) => {
    if (!activeSpace || nextMode === mode) return;
    await patchSpace({ seating_mode: nextMode });
    toast.success(`Now: ${modeLabel(nextMode)}`);
  };

  // ---- Tables ----
  const addTable = async () => {
    if (!form.name) { toast.error("Table name required"); return; }
    if (!activeSpaceId) { toast.error("Pick a space first"); return; }
    const shapeDef = SHAPES.find(s => s.value === form.shape) || SHAPES[0];
    try {
      const { data } = await api.post(`/events/${eventId}/tables`, {
        ...form, capacity: Number(form.capacity) || 8,
        width: shapeDef.defaultW, height: shapeDef.defaultH,
        x: 100 + Math.random() * 300, y: 100 + Math.random() * 200, rotation: 0,
        space_id: activeSpaceId,
      });
      setTables([...tables, data]);
      setTableDialog(false);
      setForm({ name: "", shape: "round", capacity: 8 });
      toast.success("Table added");
    } catch (e) { toast.error("Could not add"); }
  };

  const deleteTable = async (id) => {
    if (!confirm("Delete this table?")) return;
    try {
      await api.delete(`/events/${eventId}/tables/${id}`);
      setTables(tables.filter(t => t.id !== id));
      setGuests(guests.map(g => g.table_id === id ? { ...g, table_id: null } : g));
      if (selectedTable === id) setSelectedTable(null);
    } catch (e) { toast.error("Could not delete"); }
  };

  const rotateTable = async (t) => {
    const next = ((t.rotation || 0) + 15) % 360;
    setTables(tables.map(x => x.id === t.id ? { ...x, rotation: next } : x));
    try { await api.patch(`/events/${eventId}/tables/${t.id}`, { rotation: next }); } catch (e) { console.error("rotate table failed:", e); }
  };

  const assign = async (guestId, tableId) => {
    try {
      await api.patch(`/events/${eventId}/guests/${guestId}`, { table_id: tableId });
      setGuests(guests.map(g => g.id === guestId ? { ...g, table_id: tableId } : g));
    } catch (e) { toast.error("Could not assign"); }
  };

  // ---- Decor elements (persisted to the active space) ----
  const setElements = (updater) => {
    if (!activeSpace) return;
    const next = typeof updater === "function" ? updater(activeSpace.elements || []) : updater;
    patchSpace({ elements: next });
  };

  const addElement = (typeDef) => {
    const el = {
      id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type: typeDef.type,
      label: typeDef.label,
      x: 60 + Math.random() * 200, y: 60 + Math.random() * 200,
      w: typeDef.w, h: typeDef.h,
      rotation: 0,
    };
    setElements(els => [...els, el]);
  };

  const deleteElement = (id) => {
    setElements(els => els.filter(e => e.id !== id));
    if (selectedElement === id) setSelectedElement(null);
  };

  const rotateElement = (el) => {
    setElements(els => els.map(e => e.id === el.id ? { ...e, rotation: ((e.rotation || 0) + 15) % 360 } : e));
  };

  const resizeElement = (id, dims) => {
    setElements(els => els.map(e => e.id === id ? { ...e, ...dims } : e));
  };

  // ---- Custom decor element ----
  const addCustomElement = () => {
    const label = (customForm.label || "").trim();
    if (!label) { toast.error("Give this element a name"); return; }
    const el = {
      id: `el_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type: "custom",
      label,
      shape: customForm.shape,       // rect | circle | triangle
      color: customForm.color || "#0A0A0A",
      x: 80 + Math.random() * 160, y: 80 + Math.random() * 160,
      w: Math.max(30, Math.min(600, Number(customForm.w) || 120)),
      h: Math.max(30, Math.min(600, Number(customForm.h) || 80)),
      rotation: 0,
    };
    setElements(els => [...els, el]);
    setCustomOpen(false);
    setCustomForm({ label: "", shape: "rect", w: 120, h: 80, color: "#0A0A0A" });
  };

  // ---- Freehand room outline ----
  // Ramer-Douglas-Peucker polyline simplification. Small epsilon keeps curves; large makes
  // the polygon look "generated" and less noisy. ~6px works well at typical scale.
  const simplifyRDP = (points, epsilon = 6) => {
    if (!points || points.length < 3) return points || [];
    const distToSegment = (p, a, b) => {
      const dx = b.x - a.x, dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
      let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
      t = Math.max(0, Math.min(1, t));
      return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
    };
    const rec = (pts) => {
      if (pts.length < 3) return pts.slice();
      let maxD = 0, idx = 0;
      for (let i = 1; i < pts.length - 1; i++) {
        const d = distToSegment(pts[i], pts[0], pts[pts.length - 1]);
        if (d > maxD) { maxD = d; idx = i; }
      }
      if (maxD > epsilon) {
        const left = rec(pts.slice(0, idx + 1));
        const right = rec(pts.slice(idx));
        return left.slice(0, -1).concat(right);
      }
      return [pts[0], pts[pts.length - 1]];
    };
    return rec(points);
  };

  // Snap polygon edges to nearest 45° while preserving each edge's length.
  // Drift is absorbed into the closing edge, so the polygon still closes cleanly.
  const snapPolygonTo45 = (poly) => {
    if (!poly || poly.length < 3) return poly;
    const step = Math.PI / 4; // 45°
    const out = [{ x: poly[0].x, y: poly[0].y }];
    for (let i = 1; i < poly.length; i++) {
      const prev = out[out.length - 1];
      const cur = poly[i];
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const snapped = Math.round(Math.atan2(dy, dx) / step) * step;
      out.push({
        x: prev.x + len * Math.cos(snapped),
        y: prev.y + len * Math.sin(snapped),
      });
    }
    // Close the polygon by pinning the last vertex to the first (absorbs snap drift).
    if (out.length >= 3) out[out.length - 1] = { x: out[0].x, y: out[0].y };
    return out;
  };

  const startRoomStroke = (e) => {
    if (!drawMode || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    strokeRef.current = [p];
    setDrawingPoints([p]);
    e.stopPropagation();
    e.preventDefault();
  };
  const continueRoomStroke = (e) => {
    if (!drawMode || !strokeRef.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const last = strokeRef.current[strokeRef.current.length - 1];
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    // Skip micro-moves to keep the raw stroke reasonable
    if (Math.hypot(p.x - last.x, p.y - last.y) < 3) return;
    strokeRef.current.push(p);
    setDrawingPoints([...strokeRef.current]);
  };
  const finishRoomStroke = () => {
    if (!drawMode || !strokeRef.current) return;
    const raw = strokeRef.current;
    strokeRef.current = null;
    setDrawingPoints(null);
    if (raw.length < 4) { toast.error("Draw a full outline of the room"); return; }
    // Close the loop (last point → first) and simplify
    const closed = raw.slice();
    if (Math.hypot(closed[0].x - closed[closed.length - 1].x, closed[0].y - closed[closed.length - 1].y) > 1) {
      closed.push({ ...closed[0] });
    }
    const simplified = simplifyRDP(closed, 6);
    const snapped = snap45 ? snapPolygonTo45(simplified) : simplified;
    // Clamp to canvas bounds
    const W = activeSpace?.width || 900;
    const H = activeSpace?.height || 600;
    const clamped = snapped.map(pt => ({
      x: Math.max(0, Math.min(W, Math.round(pt.x))),
      y: Math.max(0, Math.min(H, Math.round(pt.y))),
    }));
    patchSpace({ room_polygon: clamped });
    setDrawMode(false);
    toast.success(`Room shape saved · ${clamped.length} points${snap45 ? " · snapped 45°" : ""}`);
  };

  const clearRoomShape = () => {
    if (!confirm("Clear the drawn room shape?")) return;
    patchSpace({ room_polygon: null });
  };

  // ---- Drag ----
  const startDrag = (e, kind, item) => {
    if (!canEdit) return;
    const rect = canvasRef.current.getBoundingClientRect();
    dragging.current = {
      kind, id: item.id,
      offsetX: e.clientX - rect.left - item.x,
      offsetY: e.clientY - rect.top - item.y,
    };
    if (kind === "table") { setSelectedTable(item.id); setSelectedElement(null); }
    else { setSelectedElement(item.id); setSelectedTable(null); }
    e.stopPropagation();
  };

  const onMove = (e) => {
    if (!dragging.current || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - dragging.current.offsetX);
    const y = Math.max(0, e.clientY - rect.top - dragging.current.offsetY);
    if (dragging.current.kind === "table") {
      setTables(ts => ts.map(t => t.id === dragging.current.id ? { ...t, x, y } : t));
    } else {
      setSpaces(prev => prev.map(s => s.id === activeSpaceId
        ? { ...s, elements: (s.elements || []).map(el => el.id === dragging.current.id ? { ...el, x, y } : el) }
        : s));
    }
  };

  const endDrag = async () => {
    if (!dragging.current) return;
    const { kind, id } = dragging.current;
    dragging.current = null;
    if (kind === "table") {
      const t = tables.find(x => x.id === id);
      if (t) { try { await api.patch(`/events/${eventId}/tables/${t.id}`, { x: t.x, y: t.y }); } catch (e) { console.error("move table failed:", e); } }
    } else if (activeSpace) {
      // Persist the final elements array for the current space
      patchSpace({ elements: activeSpace.elements });
    }
  };

  // ---- Room resize ----
  const updateRoomSize = (field, value) => {
    const v = Math.max(300, Math.min(2000, Number(value) || 900));
    patchSpace({ [field]: v });
  };

  // ---- Derived ----
  const selTable = tables.find(t => t.id === selectedTable);
  const selElement = (activeSpace?.elements || []).find(e => e.id === selectedElement);
  const seatedInSelected = selTable ? guests.filter(g => g.table_id === selTable.id) : [];
  const totalCapacity = tables.reduce((sum, t) => sum + (Number(t.capacity) || 0), 0);
  const seatedHere = guests.filter(g => tables.some(t => t.id === g.table_id)).length;
  const unseated = guests.filter(g => !g.table_id);
  const showCanvas = mode !== "standing";
  const showGuestAssignments = mode === "assigned" || mode === "rows";

  return (
    <div className="space-y-6" data-testid="seating-tab-root">
      {/* Space tab strip */}
      <div className="flex items-center gap-2 flex-wrap border-b border-hair pb-2">
        <div className="flex items-center gap-1 flex-wrap" data-testid="space-tab-strip">
          {spaces.map(sp => {
            const isActive = sp.id === activeSpaceId;
            return (
              <button
                key={sp.id}
                onClick={() => setActiveSpaceId(sp.id)}
                data-testid={`space-tab-${sp.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-sm border-b-2 transition-colors ${
                  isActive
                    ? "border-[#0A0A0A] text-app font-medium bg-surface"
                    : "border-transparent text-muted hover:text-app hover:bg-canvas"
                }`}
              >
                <MapPin size={12} strokeWidth={1.5} />
                {sp.name}
                <span className="text-[10px] uppercase tracking-widest text-muted ml-1">
                  {sp.seating_mode === "assigned" ? "" : `· ${sp.seating_mode}`}
                </span>
              </button>
            );
          })}
        </div>
        {canEdit && (
          <div className="ml-auto flex items-center gap-1">
            <Button
              onClick={() => setNewSpaceOpen(true)}
              variant="outline"
              data-testid="add-space-btn"
              className="rounded-sm h-8 text-xs"
            >
              <Plus size={12} strokeWidth={1.5} className="mr-1" /> New space
            </Button>
            {activeSpace && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="space-menu-trigger">
                    <MoreVertical size={14} strokeWidth={1.5} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-surface">
                  <DropdownMenuItem
                    onClick={() => { setRenameValue(activeSpace.name); setRenameOpen(true); }}
                    data-testid="space-rename"
                  >
                    <Pencil size={12} strokeWidth={1.5} className="mr-2" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setDupName(`${activeSpace.name} v2`); setDupCopyTables(true); setDupOpen(true); }}
                    data-testid="space-duplicate"
                  >
                    <Copy size={12} strokeWidth={1.5} className="mr-2" /> Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={deleteSpace}
                    disabled={spaces.length <= 1}
                    data-testid="space-delete"
                    className="text-red-600 focus:text-red-700"
                  >
                    <Trash2 size={12} strokeWidth={1.5} className="mr-2" /> Delete space
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>

      {/* Header + summary + mode selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Floor plan · {activeSpace?.name || "—"}</div>
          <div className="font-serif text-2xl text-app">
            {mode === "standing" ? (
              <>Standing · {guests.length} guests</>
            ) : mode === "open" ? (
              <>Open seating · {guests.length} guests · {totalCapacity} seats available</>
            ) : mode === "rows" ? (
              <>Rows · {seatedHere} of {guests.length} placed</>
            ) : (
              <>{tables.length} tables · {seatedHere} of {guests.length} guests seated</>
            )}
          </div>
        </div>
        {canEdit && activeSpace && (
          <div className="flex items-center gap-2">
            <Select value={mode} onValueChange={changeMode}>
              <SelectTrigger className="w-52 rounded-sm h-9" data-testid="space-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEATING_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {showCanvas && (
              <div className="flex items-center gap-1 bevel border-hair rounded-sm px-2 py-1">
                <span className="eyebrow">Room</span>
                <Input type="number" data-testid="room-width" className="w-16 h-7 rounded-sm text-xs border-0 focus-visible:ring-0" value={activeSpace.width || 900} onChange={(e) => updateRoomSize("width", e.target.value)} />
                <span className="text-xs text-muted">×</span>
                <Input type="number" data-testid="room-height" className="w-16 h-7 rounded-sm text-xs border-0 focus-visible:ring-0" value={activeSpace.height || 600} onChange={(e) => updateRoomSize("height", e.target.value)} />
              </div>
            )}
            {mode !== "standing" && (
              <Dialog open={tableDialog} onOpenChange={setTableDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-table-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                    <Plus size={16} strokeWidth={1.5} className="mr-2" /> {mode === "rows" ? "Add row" : "Add table"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface rounded-sm max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-app">New {mode === "rows" ? "row" : "table"}</DialogTitle>
                    <DialogDescription>
                      {mode === "rows" ? "Rows are long-shaped seat blocks. Use capacity for the number of chairs in the row." : "Add a table and drop it into the space."}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <F label="Name"><Input data-testid="table-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={mode === "rows" ? "e.g. Row A" : "e.g. Head Table / Table 4"} /></F>
                    <div className="grid grid-cols-2 gap-3">
                      <F label="Shape">
                        <Select value={form.shape} onValueChange={(v) => setForm({ ...form, shape: v })}>
                          <SelectTrigger className="rounded-sm" data-testid="table-shape-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{SHAPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </F>
                      <F label={mode === "rows" ? "Seats in row" : "Capacity"}><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></F>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setTableDialog(false)}>Cancel</Button>
                    <Button data-testid="table-submit" onClick={addTable} className="rounded-sm bg-app hover:bg-[#000000]">Add</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}
      </div>

      {/* Standing mode: reduced UX */}
      {mode === "standing" ? (
        <div className="bevel border-hair rounded-sm p-8 text-center" data-testid="standing-summary">
          <Users size={22} strokeWidth={1.2} className="text-muted mx-auto" />
          <div className="mt-3 font-serif text-3xl text-app">Standing / cocktail-style</div>
          <p className="text-sm text-muted font-light mt-1 max-w-md mx-auto">
            No fixed tables in this space. Track headcount below; use another space for seated moments.
          </p>
          <div className="mt-6 inline-flex items-center gap-4 text-sm text-app">
            <span><strong className="font-serif text-2xl">{guests.length}</strong> guests</span>
            {canEdit && (
              <div className="flex items-center gap-1 border-l border-hair pl-4">
                <span className="eyebrow">Notes</span>
                <Textarea
                  data-testid="standing-notes"
                  rows={2}
                  className="w-64 text-xs"
                  placeholder="e.g. hors d'oeuvres stations along east wall, bar in the middle"
                  defaultValue={activeSpace?.notes || ""}
                  onBlur={(e) => patchSpace({ notes: e.target.value || null })}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Decor palette */}
          {canEdit && (
            <div className="bevel border-hair rounded-sm p-3 flex items-center gap-2 flex-wrap">
              <span className="eyebrow px-2">Add decor</span>
              {DECOR_TYPES.map(d => {
                const Icon = d.icon;
                return (
                  <button
                    key={d.type}
                    onClick={() => addElement(d)}
                    data-testid={`add-decor-${d.type}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-hair hover:border-[#0A0A0A] hover:bg-canvas text-xs transition-colors"
                    title={`Add ${d.label}`}
                  >
                    <Icon size={13} strokeWidth={1.5} style={{ color: d.color }} />
                    <span className="text-app">{d.label}</span>
                  </button>
                );
              })}
              <button
                onClick={() => setCustomOpen(true)}
                data-testid="add-decor-custom"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-dashed border-[#0A0A0A] hover:bg-app hover:text-white text-xs transition-colors text-app"
                title="Add a custom shape with your own label"
              >
                <Shapes size={13} strokeWidth={1.75} />
                <span>Custom…</span>
              </button>
              <div className="ml-auto flex items-center gap-1.5">
                <button
                  onClick={() => setSnap45(s => !s)}
                  data-testid="toggle-snap-45"
                  title={snap45 ? "Snap 45° ON — edges will snap to horizontal / vertical / 45° when you finish drawing" : "Snap 45° OFF — freehand curves preserved"}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-xs transition-colors ${
                    snap45
                      ? "bg-surface-2 text-app border-[#0A0A0A]"
                      : "border-hair hover:border-[#0A0A0A] hover:bg-canvas text-muted"
                  }`}
                >
                  <Ruler size={13} strokeWidth={1.75} />
                  <span>Snap 45°</span>
                  <span className={`font-mono text-[9px] tracking-widest ${snap45 ? "text-emerald-400" : "text-faint"}`}>{snap45 ? "ON" : "OFF"}</span>
                </button>
                <button
                  onClick={() => setDrawMode(m => !m)}
                  data-testid="toggle-room-draw"
                  title={drawMode ? "Cancel drawing" : "Draw the room outline freehand"}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs transition-colors ${
                    drawMode
                      ? "bevel-btn-primary text-white border-[#0A0A0A]"
                      : "border-hair hover:border-[#0A0A0A] hover:bg-canvas text-app"
                  }`}
                >
                  <PenLine size={13} strokeWidth={1.75} />
                  <span>{drawMode ? "Drawing… click canvas" : "Draw room"}</span>
                </button>
                {(activeSpace?.room_polygon || []).length > 0 && (
                  <button
                    onClick={clearRoomShape}
                    data-testid="clear-room-shape"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-hair hover:border-[#EF4444] hover:text-[#EF4444] text-xs transition-colors text-muted"
                    title="Clear the drawn room outline"
                  >
                    <Eraser size={13} strokeWidth={1.75} />
                    <span>Clear shape</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Canvas + Side panel */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            <div className="bg-[#EEEDE7] border border-hair rounded-sm p-4 overflow-auto">
              <div
                ref={canvasRef}
                onMouseDown={drawMode ? startRoomStroke : undefined}
                onMouseMove={drawMode ? continueRoomStroke : onMove}
                onMouseUp={drawMode ? finishRoomStroke : endDrag}
                onMouseLeave={drawMode ? finishRoomStroke : endDrag}
                onClick={drawMode ? undefined : () => { setSelectedTable(null); setSelectedElement(null); }}
                data-testid="seating-canvas"
                className={`seating-canvas relative bg-surface border-2 border-[#0A0A0A] rounded-sm select-none mx-auto ${drawMode ? "cursor-crosshair" : ""}`}
                style={{ width: activeSpace?.width || 900, height: activeSpace?.height || 600 }}
              >
                {/* Room polygon overlay (persisted + live stroke) */}
                <RoomPolygonOverlay
                  width={activeSpace?.width || 900}
                  height={activeSpace?.height || 600}
                  polygon={activeSpace?.room_polygon}
                  livePoints={drawingPoints}
                  drawMode={drawMode}
                />

                {tables.length === 0 && (activeSpace?.elements || []).length === 0 && !(activeSpace?.room_polygon || []).length && !drawMode && (
                  <div className="absolute inset-0 flex items-center justify-center text-muted font-light text-center px-6 pointer-events-none">
                    Add a {mode === "rows" ? "row" : "table"} or drop in decor to start designing this space.
                  </div>
                )}
                {drawMode && (
                  <div className="absolute top-3 left-3 z-40 bevel-btn-primary text-white px-2.5 py-1 text-[10px] font-mono tracking-widest uppercase pointer-events-none">
                    Draw the outline · click & drag
                  </div>
                )}

                {(activeSpace?.elements || []).map(el => {
                  const isCustom = el.type === "custom";
                  const def = isCustom ? null : DECOR_TYPES.find(d => d.type === el.type);
                  const Icon = def?.icon || Sparkles;
                  const isSel = selectedElement === el.id;
                  const color = isCustom ? (el.color || "#0A0A0A") : (def?.color || "#71717A");
                  const shape = isCustom ? (el.shape || "rect") : null;
                  // Element shape rules for custom types
                  const borderRadius = shape === "circle" ? "50%" : "2px";
                  const isTri = shape === "triangle";
                  return (
                    <div
                      key={el.id}
                      data-testid={`decor-${el.id}`}
                      onMouseDown={(e) => !drawMode && startDrag(e, "element", el)}
                      onClick={(e) => { if (drawMode) return; e.stopPropagation(); setSelectedElement(el.id); setSelectedTable(null); }}
                      className={`absolute flex flex-col items-center justify-center text-center ${drawMode ? "pointer-events-none opacity-60" : "cursor-move"} ${isSel ? "z-30 ring-2 ring-[#0A0A0A]" : "z-10"} ${isTri ? "" : "border rounded-sm"}`}
                      style={{
                        left: el.x, top: el.y, width: el.w, height: el.h,
                        background: isTri ? "transparent" : `${color}18`,
                        borderColor: color,
                        borderStyle: el.type === "dance-floor" ? "dashed" : "solid",
                        borderRadius,
                        transform: `rotate(${el.rotation || 0}deg)`,
                        transformOrigin: "center",
                      }}
                    >
                      {isTri && (
                        <svg width={el.w} height={el.h} className="absolute inset-0 pointer-events-none">
                          <polygon
                            points={`${el.w / 2},2 ${el.w - 2},${el.h - 2} 2,${el.h - 2}`}
                            fill={`${color}18`}
                            stroke={color}
                            strokeWidth="1.5"
                          />
                        </svg>
                      )}
                      <div className="relative flex flex-col items-center justify-center">
                        {!isCustom && <Icon size={16} strokeWidth={1.5} style={{ color }} />}
                        <div className="eyebrow mt-1 px-1" style={{ color }}>{el.label}</div>
                      </div>
                    </div>
                  );
                })}

                {tables.map(t => {
                  const shape = SHAPES.find(s => s.value === t.shape) || SHAPES[0];
                  const w = t.width || shape.defaultW;
                  const h = t.height || shape.defaultH;
                  const seated = guests.filter(g => g.table_id === t.id).length;
                  const isSel = selectedTable === t.id;
                  const radius = t.shape === "round" ? "50%" : t.shape === "oval" ? "50%" : "2px";
                  // In open/rows mode we still show tables but capacity is the primary signal
                  const openMode = mode === "open" || mode === "rows";
                  const utilization = t.capacity > 0 ? seated / t.capacity : 0;
                  const openBorder = utilization > 1 ? "border-red-500" : utilization >= 1 ? "border-amber-500" : "border-[#0A0A0A]";
                  return (
                    <div
                      key={t.id}
                      data-testid={`table-${t.id}`}
                      onMouseDown={(e) => startDrag(e, "table", t)}
                      onClick={(e) => { e.stopPropagation(); setSelectedTable(t.id); setSelectedElement(null); }}
                      className={`absolute cursor-move flex items-center justify-center text-center transition-shadow ${isSel ? "z-40" : "z-20"}`}
                      style={{
                        left: t.x, top: t.y, width: w, height: h,
                        transform: `rotate(${t.rotation || 0}deg)`,
                        transformOrigin: "center",
                      }}
                    >
                      <div
                        className={`w-full h-full flex items-center justify-center border-2 ${isSel ? "border-[#0A0A0A] shadow-lg" : openMode ? openBorder : "border-[#0A0A0A]"} bg-surface text-center px-2`}
                        style={{ borderRadius: radius }}
                      >
                        <div style={{ transform: `rotate(${-(t.rotation || 0)}deg)` }}>
                          <div className="font-serif text-base text-app leading-none">{t.name}</div>
                          <div className="eyebrow mt-1">
                            {mode === "open" ? `${t.capacity} seats` : `${seated}/${t.capacity}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Side panel */}
            <div className="space-y-4">
              {mode === "open" && (
                <div className="bevel border-hair rounded-sm p-5" data-testid="open-seating-summary">
                  <div className="eyebrow mb-1 flex items-center gap-1.5"><ArmchairIcon size={11} strokeWidth={1.5}/> Open seating</div>
                  <div className="font-serif text-2xl text-app">{totalCapacity} seats · {guests.length} guests</div>
                  <p className="text-sm text-muted font-light mt-1">
                    Guests aren&apos;t assigned to a specific table in this space — sit-where-you-like. The border color signals capacity: green ok, amber full, red over.
                  </p>
                </div>
              )}

              {selTable ? (
                <div className="bevel border-hair rounded-sm p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="eyebrow">{mode === "rows" ? "Row" : "Table"}</div>
                      <div className="font-serif text-2xl text-app">{selTable.name}</div>
                      <div className="text-xs text-muted mt-1">
                        {showGuestAssignments ? `${seatedInSelected.length}/${selTable.capacity}` : `${selTable.capacity} seats`} · {selTable.shape}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => rotateTable(selTable)} data-testid={`rotate-table-${selTable.id}`} title="Rotate 15°" className="text-muted hover:text-app p-1"><RotateCw size={14} strokeWidth={1.5} /></button>
                        <button onClick={() => deleteTable(selTable.id)} data-testid={`delete-table-${selTable.id}`} className="text-muted hover:text-[#EF4444] p-1"><Trash2 size={14} strokeWidth={1.5} /></button>
                      </div>
                    )}
                  </div>
                  {showGuestAssignments ? (
                    <div className="space-y-1.5">
                      {seatedInSelected.map(g => (
                        <div key={g.id} className="flex items-center justify-between text-sm bg-canvas px-2 py-1.5 rounded-sm">
                          <span className="truncate text-app">{g.name} {g.party_size > 1 && <span className="text-muted">(+{g.party_size - 1})</span>}</span>
                          {canEdit && <button onClick={() => assign(g.id, null)} className="text-xs text-muted hover:text-[#EF4444]">remove</button>}
                        </div>
                      ))}
                      {seatedInSelected.length === 0 && <div className="text-xs text-muted font-light py-3">No one seated yet.</div>}
                    </div>
                  ) : (
                    <div className="text-xs text-muted font-light py-2" data-testid="open-table-no-picker">
                      Open seating — no guest assignments in this space.
                    </div>
                  )}
                </div>
              ) : selElement ? (
                <div className="bevel border-hair rounded-sm p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="eyebrow">Decor element</div>
                      <div className="font-serif text-2xl text-app">{selElement.label}</div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => rotateElement(selElement)} data-testid={`rotate-decor-${selElement.id}`} className="text-muted hover:text-app p-1"><RotateCw size={14} strokeWidth={1.5} /></button>
                        <button onClick={() => deleteElement(selElement.id)} data-testid={`delete-decor-${selElement.id}`} className="text-muted hover:text-[#EF4444] p-1"><Trash2 size={14} strokeWidth={1.5} /></button>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <F label="Width">
                        <Input type="number" value={selElement.w} onChange={(e) => resizeElement(selElement.id, { w: Number(e.target.value) || 40 })} className="rounded-sm h-8 text-sm" />
                      </F>
                      <F label="Height">
                        <Input type="number" value={selElement.h} onChange={(e) => resizeElement(selElement.id, { h: Number(e.target.value) || 40 })} className="rounded-sm h-8 text-sm" />
                      </F>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bevel border-hair rounded-sm p-5">
                  <div className="eyebrow mb-2">Design this space</div>
                  <p className="text-sm text-muted font-light">
                    {mode === "rows"
                      ? "Add a row (long-shape works well), set the seat count, and drop it in place. Repeat for each row."
                      : mode === "open"
                      ? "Add tables so guests can pick seats. Guest assignments live in other spaces."
                      : "Set room dimensions above. Drop in decor to sketch the layout, then add tables and drag them into place."}
                  </p>
                </div>
              )}

              {showGuestAssignments && (
                <div className="bevel border-hair rounded-sm p-5">
                  <div className="eyebrow mb-3">Unseated ({unseated.length})</div>
                  <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                    {unseated.map(g => (
                      <div key={g.id} className="flex items-center justify-between text-sm py-1.5 border-b border-hair/60 last:border-b-0">
                        <span className="truncate text-app">{g.name}</span>
                        {canEdit && (
                          <Select value="none" onValueChange={(v) => v !== "none" && assign(g.id, v)}>
                            <SelectTrigger className="w-28 h-7 rounded-sm text-xs" data-testid={`assign-guest-${g.id}`}><SelectValue placeholder="Seat…" /></SelectTrigger>
                            <SelectContent>
                              {tables.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ))}
                    {unseated.length === 0 && <div className="text-xs text-muted font-light py-3">Everyone&apos;s got a seat.</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* --- New Space Dialog --- */}
      <Dialog open={newSpaceOpen} onOpenChange={setNewSpaceOpen}>
        <DialogContent className="bg-surface rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-app">Add a space</DialogTitle>
            <DialogDescription>Each space has its own floor plan and seating mode — ceremony, reception, cocktail patio, etc.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <F label="Name">
              <Input
                data-testid="new-space-name"
                value={newSpaceForm.name}
                onChange={(e) => setNewSpaceForm({ ...newSpaceForm, name: e.target.value })}
                placeholder="e.g. Ceremony aisle, Reception hall, Rooftop cocktails"
              />
            </F>
            <F label="Seating mode">
              <Select value={newSpaceForm.seating_mode} onValueChange={(v) => setNewSpaceForm({ ...newSpaceForm, seating_mode: v })}>
                <SelectTrigger className="rounded-sm" data-testid="new-space-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEATING_MODES.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      <div>
                        <div>{m.label}</div>
                        <div className="text-[10px] text-muted">{m.helper}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            {newSpaceForm.seating_mode !== "standing" && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Room width (px)">
                  <Input type="number" value={newSpaceForm.width} onChange={(e) => setNewSpaceForm({ ...newSpaceForm, width: e.target.value })} />
                </F>
                <F label="Room height (px)">
                  <Input type="number" value={newSpaceForm.height} onChange={(e) => setNewSpaceForm({ ...newSpaceForm, height: e.target.value })} />
                </F>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewSpaceOpen(false)}>Cancel</Button>
            <Button
              data-testid="new-space-submit"
              onClick={createSpace}
              className="rounded-sm bg-app hover:bg-[#000000]"
            >Add space</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Rename Dialog --- */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="bg-surface rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-app">Rename space</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            data-testid="space-rename-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>Cancel</Button>
            <Button onClick={renameSpace} data-testid="space-rename-submit" className="rounded-sm bg-app hover:bg-[#000000]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Duplicate Dialog --- */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="bg-surface rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-app">Duplicate space</DialogTitle>
            <DialogDescription>Creates a copy of &ldquo;{activeSpace?.name}&rdquo; — great for what-if layouts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <F label="Name for the copy">
              <Input value={dupName} onChange={(e) => setDupName(e.target.value)} data-testid="space-dup-name" />
            </F>
            <label className="flex items-center gap-2 text-sm text-app">
              <input
                type="checkbox"
                checked={dupCopyTables}
                onChange={(e) => setDupCopyTables(e.target.checked)}
                data-testid="space-dup-copy-tables"
              />
              Also copy tables (guests will not be re-assigned)
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupOpen(false)}>Cancel</Button>
            <Button onClick={duplicateSpace} data-testid="space-dup-submit" className="rounded-sm bg-app hover:bg-[#000000]">Duplicate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Custom Element Dialog --- */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="bg-surface rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-app">Custom element</DialogTitle>
            <DialogDescription>Drop in your own labeled shape — a photo booth, coat check, kids&apos; corner, anything.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <F label="Label">
              <Input
                data-testid="custom-element-label"
                value={customForm.label}
                onChange={(e) => setCustomForm({ ...customForm, label: e.target.value })}
                placeholder="e.g. Photo booth, Coat check, Kids' corner"
                autoFocus
              />
            </F>
            <F label="Shape">
              <Select value={customForm.shape} onValueChange={(v) => setCustomForm({ ...customForm, shape: v })}>
                <SelectTrigger className="rounded-sm" data-testid="custom-element-shape"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rect">Rectangle</SelectItem>
                  <SelectItem value="circle">Circle</SelectItem>
                  <SelectItem value="triangle">Triangle</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Width (px)">
                <Input type="number" data-testid="custom-element-width" value={customForm.w} onChange={(e) => setCustomForm({ ...customForm, w: e.target.value })} />
              </F>
              <F label="Height (px)">
                <Input type="number" data-testid="custom-element-height" value={customForm.h} onChange={(e) => setCustomForm({ ...customForm, h: e.target.value })} />
              </F>
            </div>
            <F label="Accent color">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  data-testid="custom-element-color"
                  value={customForm.color}
                  onChange={(e) => setCustomForm({ ...customForm, color: e.target.value })}
                  className="h-9 w-14 border border-hair rounded-sm cursor-pointer bg-surface"
                />
                <span className="font-mono text-xs text-muted">{customForm.color?.toUpperCase()}</span>
              </div>
            </F>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button onClick={addCustomElement} data-testid="custom-element-submit" className="rounded-sm bg-app hover:bg-[#000000]">Add to floor plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Renders the drawn room outline (persisted polygon) + the live stroke while drawing.
function RoomPolygonOverlay({ width, height, polygon, livePoints, drawMode }) {
  const persisted = Array.isArray(polygon) && polygon.length >= 3 ? polygon : null;
  const live = Array.isArray(livePoints) && livePoints.length >= 2 ? livePoints : null;
  if (!persisted && !live) return null;
  const toStr = (pts) => pts.map(p => `${p.x},${p.y}`).join(" ");
  return (
    <svg
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none z-0"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      {persisted && !drawMode && (
        <polygon
          points={toStr(persisted)}
          fill="rgba(10,10,10,0.03)"
          stroke="#0A0A0A"
          strokeWidth="1.5"
          strokeDasharray="6 4"
        />
      )}
      {live && (
        <polyline
          points={toStr(live)}
          fill="none"
          stroke="#DC2626"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
